/**
 * useTutorialPlayer — Controls the animated tutorial walkthrough player.
 * Manages play/pause, slide navigation, auto-advance timing, and
 * persistence (hasWatched) to localStorage.
 *
 * The tutorial is always opt-in — never auto-triggers.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { TUTORIAL_SLIDES } from "@client/components/onboarding/tutorial-slides";

const STORAGE_KEY = "cw_tutorial_watched";

export interface TutorialPlayerState {
  isOpen: boolean;
  currentSlide: number;
  isPlaying: boolean;
  hasWatched: boolean;
  totalSlides: number;
  progress: number;
  /** Elapsed ms within the current slide (for progress bar within slide) */
  slideElapsed: number;
  open: () => void;
  close: () => void;
  play: () => void;
  pause: () => void;
  nextSlide: () => void;
  prevSlide: () => void;
  restart: () => void;
}

export function useTutorialPlayer(): TutorialPlayerState {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [slideElapsed, setSlideElapsed] = useState(0);
  const [hasWatched, setHasWatched] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const totalSlides = TUTORIAL_SLIDES.length;
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const slideStartRef = useRef<number>(Date.now());

  // Overall progress: which slide we're on as a percentage
  const progress =
    totalSlides > 0
      ? Math.round(((currentSlide + 1) / totalSlides) * 100)
      : 0;

  // Auto-advance timer
  useEffect(() => {
    if (!isPlaying || !isOpen) return;

    const slide = TUTORIAL_SLIDES[currentSlide];
    if (!slide) return;

    slideStartRef.current = Date.now();
    setSlideElapsed(0);

    // Tick every 100ms for smooth progress within slide
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - slideStartRef.current;
      setSlideElapsed(elapsed);

      if (elapsed >= slide.durationMs) {
        clearInterval(timerRef.current);
        // Advance to next slide or finish
        if (currentSlide < totalSlides - 1) {
          setCurrentSlide((s) => s + 1);
        } else {
          // Tutorial complete
          setIsPlaying(false);
          setHasWatched(true);
          try {
            localStorage.setItem(STORAGE_KEY, "true");
          } catch {
            // ignore
          }
        }
      }
    }, 100);

    return () => clearInterval(timerRef.current);
  }, [isPlaying, isOpen, currentSlide, totalSlides]);

  const open = useCallback(() => {
    setCurrentSlide(0);
    setSlideElapsed(0);
    setIsOpen(true);
    setIsPlaying(true);
  }, []);

  const close = useCallback(() => {
    setIsPlaying(false);
    setIsOpen(false);
    clearInterval(timerRef.current);
  }, []);

  const play = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const pause = useCallback(() => {
    setIsPlaying(false);
    clearInterval(timerRef.current);
  }, []);

  const nextSlide = useCallback(() => {
    clearInterval(timerRef.current);
    setSlideElapsed(0);
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide((s) => s + 1);
    } else {
      // Reached end
      setIsPlaying(false);
      setHasWatched(true);
      try {
        localStorage.setItem(STORAGE_KEY, "true");
      } catch {
        // ignore
      }
    }
  }, [currentSlide, totalSlides]);

  const prevSlide = useCallback(() => {
    clearInterval(timerRef.current);
    setSlideElapsed(0);
    setCurrentSlide((s) => Math.max(0, s - 1));
  }, []);

  const restart = useCallback(() => {
    clearInterval(timerRef.current);
    setCurrentSlide(0);
    setSlideElapsed(0);
    setIsPlaying(true);
  }, []);

  return {
    isOpen,
    currentSlide,
    isPlaying,
    hasWatched,
    totalSlides,
    progress,
    slideElapsed,
    open,
    close,
    play,
    pause,
    nextSlide,
    prevSlide,
    restart,
  };
}
